

import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Backdrop,
  Box,
  Button,
  Chip,
  Fade,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  MenuItem,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import SaveIcon from "@mui/icons-material/Save";
import ApiCallService from "../../../Service/ApiCallService";
import { message } from "antd";
import {
  followUpCategories,
  normalizeFollowUpCategory,
} from "./followUpCategories";

const queryStatuses = [
  // "Queries Received",
  "Completed",
  "Pending",
  "On Hold",
  "NA",
];

const touchPointTypes = [
  { value: "CALL", label: "Call" },
  { value: "MESSAGE", label: "Message" },
  { value: "IN_PERSON_MEET", label: "In Person Meet" },
];

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

const getMemberId = (value) =>
  value?.memberId || value?.userId || value?._id || "";

const getMemberName = (value) =>
  value?.memberName ||
  `${value?.userFirstName || ""} ${value?.userLastName || ""}`.trim() ||
  "Member";

const emptyGroupMembers = [];

const CallFollowUpModal = ({
  open,
  onClose,
  member,
  members = [],
  groupMembers = emptyGroupMembers,
  groupLabel = "",
  onRemoveGroupMember,
  onSaved,
  record = null,
}) => {
  const [selectedMember, setSelectedMember] = useState(member || null);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [note, setNote] = useState("");
  const [callDateTime, setCallDateTime] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [callDirection, setCallDirection] = useState("OUTGOING");
  const [touchPointType, setTouchPointType] = useState("CALL");
  const [category, setCategory] = useState("Request");
  const [queryStatus, setQueryStatus] = useState("NA");
  const [saving, setSaving] = useState(false);

  const theme = useTheme()

  const getLocalDateTimeValue = () => {
    const now = new Date();

    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  useEffect(() => {
    if (open) {
      setNote(record?.note || "");
      setCallDateTime(record ? toDateTimeInputValue(record.dateTime) : getLocalDateTimeValue());
      setFollowUpDate(toDateInputValue(record?.nextFollowUpDate));
      setCallDirection(["OUTGOING", "INCOMING"].includes(record?.callDirection) ? record.callDirection : "OUTGOING");
      setTouchPointType(record?.touchPointType || "CALL");
      setCategory(normalizeFollowUpCategory(record?.category) || "Request");
      setQueryStatus(record?.queryStatus || "NA");
      const resolvedMember = member || (typeof record?.committeeMemberId === "object" ? record.committeeMemberId : null) || (record?.participantMembers?.[0] ? record.participantMembers[0] : null);
      setSelectedMember(resolvedMember || null);

      const resolvedGroup = groupMembers && groupMembers.length > 0
        ? groupMembers
        : (record?.callType === "GROUP" && record?.participantMembers ? record.participantMembers : []);
      setSelectedGroupMembers(resolvedGroup);
    }
  }, [open, record, member, groupMembers]);

  const isGroupCall = record ? (record?.callType === "GROUP" || selectedGroupMembers.length > 0) : selectedGroupMembers.length > 0;
  const fullName = getMemberName(selectedMember);

  const handleRemoveSelectedGroupMember = (memberId) => {
    setSelectedGroupMembers((prev) => prev.filter((item) => getMemberId(item) !== memberId));
    onRemoveGroupMember?.(memberId);
  };

  const handleClose = () => {
    if (!saving) {
      onClose?.();
    }
  };

  const handleSave = async () => {
    if (!callDateTime) {
      message.warning("Please select date and time");
      return;
    }

    if (!touchPointType) {
      message.warning("Please select touch-point type");
      return;
    }

    if (!callDirection) {
      message.warning("Please select direction");
      return;
    }

    if (!category) {
      message.warning("Please select category");
      return;
    }

    if (!queryStatus) {
      message.warning("Please select query status");
      return;
    }

    const resolvedCallType = isGroupCall ? "GROUP" : "INDIVIDUAL";
    const shouldSaveAsGroup = resolvedCallType === "GROUP";
    const participantIds = isGroupCall
      ? selectedGroupMembers.map(getMemberId).filter(Boolean)
      : [
          getMemberId(selectedMember) ||
          getMemberId(member) ||
          (typeof record?.committeeMemberId === "string" ? record.committeeMemberId : getMemberId(record?.committeeMemberId))
        ].filter(Boolean);

    if (shouldSaveAsGroup && participantIds.length < 2) {
      message.warning("Please keep at least two members for a group call");
      return;
    }

    const currentUserId = localStorage.getItem("userId") || "";
    if (!isGroupCall && !record?._id) {
      const selectedId = getMemberId(selectedMember) || getMemberId(member);
      if (selectedId && currentUserId && selectedId === currentUserId) {
        message.warning("You cannot log a touch-point with yourself");
        return;
      }
    }

    if (!participantIds.length) {
      message.warning("Please select NEC member");
      return;
    }

    const payload = {
      committeeMemberId: participantIds[0],
      callType: resolvedCallType,
      participants: participantIds,
      groupLabel: shouldSaveAsGroup ? groupLabel : "",
      note: note.trim(),

      // Converts selected local time to UTC before saving in MongoDB
      dateTime: new Date(callDateTime).toISOString(),

      nextFollowUpDate: followUpDate ? new Date(followUpDate).toISOString() : null,
      callDirection,
      touchPointType,
      category,
      queryStatus,
    };

    try {
      setSaving(true);

      if (record?._id) {
        await ApiCallService.put(
          `${process.env.REACT_APP_BASE_URL}/followUp/${record._id}`,
          payload
        );
      } else {
        await ApiCallService.post(
          `${process.env.REACT_APP_BASE_URL}/followUp`,
          payload
        );
      }

      message.success(record?._id ? "Touch-point updated successfully!" : "Touch-point saved successfully!");

      setNote("");
      setFollowUpDate("");
      await onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("Failed to save touch-point", err);
      message.error("Failed to save touch-point");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 350,
          sx: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(15,23,42,0.45)",
          },
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            // position: "absolute",
            // top: "10%",
            // left: "5%",
            // transform: "translate(-50%, -50%)",
            width: { xs: "94%", sm: isGroupCall ? 620 : 460 },
            maxWidth: "94vw",
            maxHeight: { xs: "92vh", sm: "88vh" },
            bgcolor: "#fff",
            borderRadius: 2,
            mx: "auto",
            mt: { xs: "4vh", sm: "4vh" },
            boxShadow: "0 24px 70px rgba(15,23,42,0.28)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            animation: "modalPop 0.32s ease both",
            "@keyframes modalPop": {
              from: {
                opacity: 0,
                transform: "translateY(20px) scale(0.96)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          }}
        >
          <Box
            sx={{
              p: 3,
              flexShrink: 0,
              background: `linear-gradient(135deg, #d1e9f3 0%, ${theme.palette.accent.followUpLight} 100%)`,
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                width: 160,
                height: 160,
                borderRadius: "50%",
                right: -55,
                top: -70,
                background: "rgba(255,255,255,0.16)",
              },
            }}
          >
            {/* <IconButton
              onClick={handleClose}
              disabled={saving}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                cursor: saving ? "not-allowed" : "pointer",
                color: "#ffffff",
                bgcolor: "rgba(235, 20, 20, 0.14)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.24)",
                },
              }}
            >
              <CloseIcon />
            </IconButton> */}

            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#fff",
                  color: theme.palette.accent.followUpLight,
                  fontWeight: 800,
                }}
              >
                <PermPhoneMsgIcon sx={{ height: "1.8rem", width: "1.8rem" }} />
              </Avatar>

              <Box sx={{ minWidth: 0, flex: 1, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                <Typography variant="h6" fontWeight={800} noWrap>
                  {isGroupCall ? "Group Touch-point" : record?._id ? "Edit Touch-point" : "Touch-points"}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                  {isGroupCall
                    ? `${selectedGroupMembers.length} selected member${selectedGroupMembers.length === 1 ? "" : "s"}`
                    : fullName}
                </Typography>              

                {isGroupCall && (
                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: { xs: 82, sm: 104 },
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                      {selectedGroupMembers.map((item) => {
                        const memberId = getMemberId(item);

                        return (
                          <Chip
                            key={memberId}
                            size="small"
                            label={getMemberName(item)}
                            onDelete={() => handleRemoveSelectedGroupMember(memberId)}
                            sx={{
                              maxWidth: 180,
                              borderRadius: 1,
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "#fff",
                              fontWeight: 700,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                              "& .MuiChip-deleteIcon": {
                                color: "rgba(255,255,255,0.82)",
                                "&:hover": { color: "#fff" },
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 3, flex: 1, minHeight: 0, overflowY: "auto", postion: "relative" }}>
            {!record?._id && !isGroupCall && members.length > 0 && (
              <Autocomplete
                options={members.filter((m) => {
                  const currentUserId = localStorage.getItem("userId") || "";
                  const mId = getMemberId(m);
                  return mId && currentUserId ? mId !== currentUserId : true;
                })}
                value={selectedMember}
                getOptionLabel={(option) =>
                  option?.memberName ||
                  `${option?.userFirstName || ""} ${option?.userLastName || ""}`.trim()
                }
                onChange={(_, value) => setSelectedMember(value)}
                sx={{ mb: 2 }}
                renderInput={(params) => (
                  <TextField {...params} label="NEC Member" required />
                )}
              />
            )}

            <TextField
              fullWidth
              required
              type="datetime-local"
              label="Date & Time"
              InputLabelProps={{ shrink: true }}
              value={callDateTime}
              onChange={(e) => setCallDateTime(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              {isGroupCall ? (
                <TextField
                  fullWidth
                  label="Call Type"
                  required
                  value="Group Call"
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                />
              ) : (
                <TextField
                  select
                  fullWidth
                  label="Touch-point Type"
                  required
                  value={touchPointType}
                  onChange={(e) => setTouchPointType(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                >
                  {touchPointTypes.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                required
                fullWidth
                label="Direction"
                value={callDirection}
                onChange={(e) => setCallDirection(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              >
                <MenuItem value="OUTGOING">Outgoing</MenuItem>
                <MenuItem value="INCOMING">Incoming</MenuItem>
                {/* <MenuItem value="GROUP">Group Call</MenuItem> */}
              </TextField>

              <TextField
                select
                fullWidth
                required
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              >
                {followUpCategories.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              fullWidth
              multiline
              autoFocus
              rows={4}
              label="Conversation Notes"
              placeholder="Write what was discussed in the call..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            <TextField
              select
              fullWidth
              required
              label="Query Status"
              value={queryStatus}
              onChange={(e) => setQueryStatus(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            >
              {queryStatuses.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              type="date"
              label="Next Touch-point"
              InputLabelProps={{ shrink: true }}
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                },
              }}
            />

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.2}
              sx={{
                position: "sticky",
                bottom: "-24px",
                zIndex: 2,
                mx: -3,
                mb: -3,
                mt: 1,
                px: 3,
                py: 2,
                bgcolor: "#fff",
                borderTop: "1px solid #e5eef9",
              }}
            >
              <Button
                onClick={handleClose}
                disabled={saving}
                sx={{
                  borderRadius: 1,
                  color:theme.palette.accent.followUpLight,
                  px: 2.5,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover":{
                    color:"#fff",
                    backgroundColor:theme.palette.accent.followUpLight,
                  }
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={<SaveIcon />}
                sx={{
                  backgroundColor:theme.palette.accent.followUpLight,
                  borderRadius: 1,
                  px: 2.8,
                  textTransform: "none",
                  fontWeight: 800,
                  "&:hover":{
                    color:"#fff",
                    backgroundColor:theme.palette.accent.followUpLight,
                  }
                }}
              >
                {saving ? "Saving..." : record?._id ? "Update Touch-point" : "Save Touch-point"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CallFollowUpModal;
